/**
 * 会话 / 群 / 成员 / 拉 AI / 历史 API（架构 §7.1 / T09 / T12 / T16）
 *
 * 严格对齐 server/src/routes/conversation.routes.ts：
 *  - GET  /conversations                 -> Conversation[]
 *  - POST /conversations        { name } -> { id }        （建群）
 *  - POST /conversations/dm     { targetUserId } -> { id } （发起私聊，按 userId）
 *  - GET  /conversations/:id/members        -> Member[]
 *  - POST /conversations/:id/members { userId } -> { ok: true }
 *  - POST /conversations/:id/ai            -> { ok: true } （拉知会入群，无需 body）
 *  - GET  /conversations/:id/messages?cursor=&limit= -> { messages: Message[], cursor }
 *
 * 注：服务端历史接口把 AIFile 内联到 message.files；前端渲染文件卡片即读此字段。
 *
 * 关于"按用户名解析 userId"：
 *   "发起私聊 / 添加成员" 需要 userId，后端已实现用户名查询接口
 *   GET /users/lookup?username=（server/src/routes/user.routes.ts，/api/v1/users/lookup
 *   与 /api/v1/user/lookup 均可访问）。lookupUserByUsername 即调用该接口，前端 DM 流程
 *   可直接按账号解析 userId 发起私聊。
 */
import { get, post, patch } from './client';
import type { Conversation, Member, Message } from '../types/models';

export async function getConversations(): Promise<Conversation[]> {
  return get<Conversation[]>('/conversations');
}

export async function createGroup(
  name: string,
  templateId?: string,
): Promise<{ id: number }> {
  return post<{ id: number }>('/conversations', {
    name,
    ...(templateId ? { templateId } : {}),
  });
}

export async function createDM(targetUserId: number): Promise<{ id: number }> {
  return post<{ id: number }>('/conversations/dm', { targetUserId });
}

/** 创建"与知会"的 AI 私聊会话：POST /conversations/dm-ai */
export async function createAiDm(): Promise<{ id: number }> {
  return post<{ id: number }>('/conversations/dm-ai', {});
}

export async function getMembers(conversationId: number): Promise<Member[]> {
  return get<Member[]>(`/conversations/${conversationId}/members`);
}

export async function addMember(
  conversationId: number,
  userId: number,
): Promise<{ ok: true }> {
  return post<{ ok: true }>(`/conversations/${conversationId}/members`, { userId });
}

/** 拉知会入群：POST /conversations/:id/ai（路由忽略 body，仅需登录态） */
export async function addAi(conversationId: number): Promise<{ ok: true }> {
  return post<{ ok: true }>(`/conversations/${conversationId}/ai`, {});
}

/** 置顶 / 取消置顶：PATCH /conversations/:id/pin { pinned }（CP14） */
export async function pinConversation(
  conversationId: number,
  pinned: boolean,
): Promise<{ ok: true }> {
  return patch<{ ok: true }>(`/conversations/${conversationId}/pin`, { pinned });
}

/** 标记会话已读：POST /conversations/:id/read（CP14 未读清零） */
export async function markRead(conversationId: number): Promise<{ ok: true }> {
  return post<{ ok: true }>(`/conversations/${conversationId}/read`, {});
}

/** 搜索会话/消息：GET /conversations/search?q=（CP14） */
export async function searchConversations(q: string): Promise<Conversation[]> {
  return get<Conversation[]>('/conversations/search', { q });
}

/** U1：切换群级 AI 模式：PATCH /conversations/:id/ai-mode { mode } */
export async function setAiMode(
  conversationId: number,
  mode: 'quiet' | 'partner' | 'host',
): Promise<{ ai_mode: string }> {
  return patch<{ ai_mode: string }>(`/conversations/${conversationId}/ai-mode`, { mode });
}

export interface MessagesResult {
  messages: Message[];
  cursor: number | null;
}

export async function getMessages(
  conversationId: number,
  cursor?: number,
  limit = 50,
): Promise<MessagesResult> {
  const params: Record<string, unknown> = { limit };
  if (cursor !== undefined) params.cursor = cursor;
  return get<MessagesResult>(`/conversations/${conversationId}/messages`, params);
}

export async function sendMessageRest(
  conversationId: number,
  body: {
    content: string;
    attachments?: import('../types/models').AttachmentInput[];
    hasImage?: boolean;
    replyTo?: number | null;
  },
): Promise<Message> {
  return post<Message>(`/conversations/${conversationId}/messages`, body);
}

/**
 * 预留：用户名 -> 用户解析（供发起私聊/添加成员）。
 * 后端已实现 GET /users/lookup?username= （见 server/src/routes/user.routes.ts）。
 */
export async function lookupUserByUsername(username: string): Promise<{ id: number; username: string }> {
  return get<{ id: number; username: string }>('/users/lookup', { username });
}

// ============================================================
// U2：周摘要（按需生成 + 历史列表）
// 严格对齐 server/src/routes/conversation.routes.ts：/conversations/:id/weekly-summary
// ============================================================

export interface GenerateWeeklyResult {
  id: number;
  content: string;
  week_start: string;
  week_end: string;
}

/** 生成周摘要：POST /conversations/:id/weekly-summary { locale? } */
export async function generateWeeklySummary(
  conversationId: number,
  locale?: 'zh' | 'en',
): Promise<GenerateWeeklyResult> {
  return post<GenerateWeeklyResult>(`/conversations/${conversationId}/weekly-summary`, {
    locale,
  });
}

/** 周摘要历史：GET /conversations/:id/weekly-summary?limit= */
export async function getWeeklySummaries(
  conversationId: number,
  limit = 5,
): Promise<import('../types/models').WeeklyDigest[]> {
  return get<import('../types/models').WeeklyDigest[]>(
    `/conversations/${conversationId}/weekly-summary`,
    { limit },
  );
}


